// The manager's home.
//
// Round 45 (Nathan: "more like Zanetto Tools"): ZT's dashboard on top, the
// greeting and date, the Quotes and Jobs cards, Announcements, and the Recent
// Quotes and Active Jobs cards (components/home/HomeDashboard.tsx). Below it,
// Dayboard's own sections in the order parity pass 2 argued for:
//
//   1. what the board is currently getting WRONG (needs re-planning),
//   2. what needs doing before it goes wrong (reminders),
//   3. what is happening TODAY,
//   4. timesheets, and the form submissions that came in lately (jobs and
//      announcements now have their own cards above),
//   5. the getting-started checklist, which matters on day one and never
//      again — so it is last.
//
// One request, not five. On a phone on a site the difference is the page
// arriving at once rather than in pieces.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api';
import { scheduling, type Booking } from '../schedulingApi';
import { home, type HomeData } from '../homeApi';
import { useSession } from '../session';
import { dateRangeLabel } from '../lib/dates';
import { Banner } from '../ui';
import { Modal } from '../components/ui';
import { ReminderGroups } from '../components/ReminderList';
import { SampleDataPanel } from '../components/SampleDataPanel';
import { HomeDashboard } from '../components/home/HomeDashboard';
import { HomeTimesheets } from '../components/HomeTimesheets';

type Flagged = Booking & {
  jobNumber: string | null;
  jobTitle: string | null;
  supplierName: string | null;
  resourceName: string;
};

export function HomePage() {
  const { user, tenant, refresh } = useSession();
  const [flagged, setFlagged] = useState<Flagged[]>([]);
  const [data, setData] = useState<HomeData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingThresholds, setEditingThresholds] = useState(false);
  const isAdmin = user?.role === 'admin';

  const reload = useCallback(() => {
    // A FAILED LOAD USED TO LOOK EXACTLY LIKE A QUIET DAY.
    //
    // Everything below falls back to an empty list, so an outage — or a phone
    // that had wandered out of signal — rendered "Nothing booked for today" on
    // the screen a supervisor opens to find out where the crew is going. A
    // wrong answer delivered confidently, on the one page nobody would think to
    // reload.
    void home.load().then(
      (result) => {
        setData(result);
        setLoadError(null);
      },
      (err: unknown) => {
        setData(null);
        setLoadError(
          err instanceof ApiError
            ? err.message
            : 'Could not load today. Check your connection and try again.'
        );
      }
    );
    void scheduling
      .needsReplanning()
      .then((r) => setFlagged(r.bookings))
      .catch(() => setFlagged([]));
  }, []);

  useEffect(reload, [reload]);

  if (!user || !tenant) return null;

  const counts = data?.checklist ?? {
    resources: 0,
    jobs: 0,
    bookings: 0,
    posts: 0,
    meters: 0,
    publishedForms: 0,
    invited: false,
  };
  const invited = counts.invited;
  const reminders = data?.reminders ?? { overdue: [], today: [], upcoming: [] };

  return (
    <>
      {tenant.subscriptionStatus === 'trialing' && (
        <Banner kind="ok">
          {tenant.trialDaysRemaining} day{tenant.trialDaysRemaining === 1 ? '' : 's'} left in your
          trial. Nothing is charged until you add a card, and nothing is deleted if you don't.
        </Banner>
      )}

      {!user.emailVerified && <VerifyPrompt onVerified={refresh} />}

      {loadError && (
        <div className="banner-error mb-4" role="alert">
          {loadError}{' '}
          <button className="link" onClick={reload}>
            Try again
          </button>
        </div>
      )}

      <HomeDashboard data={data} userName={user.name} isAdmin={isAdmin} />

      {flagged.length > 0 && (
        <section className="panel needs-replanning">
          <h1>Needs re-planning</h1>
          <p className="muted">
            Someone became unavailable after these were booked. The bookings are still there; they
            just need moving or releasing.
          </p>
          <div className="-mx-6 overflow-x-auto border-y border-gray-200">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Job</th>
                  <th>Dates</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map((booking) => (
                  <tr key={booking.id}>
                    <td className="font-medium">{booking.resourceName}</td>
                    <td>
                      {/* Null on a maintenance booking (migration 013): it is
                          flagged exactly like a job booking when its machine
                          is made unavailable, and it has a supplier instead. */}
                      {booking.jobId ? (
                        <Link to={`/app/jobs/${booking.jobId}`} className="table-link">
                          {booking.jobNumber} · {booking.jobTitle}
                        </Link>
                      ) : (
                        <span className="table-muted">
                          Maintenance{booking.supplierName ? ` · ${booking.supplierName}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="table-muted whitespace-nowrap">
                      {dateRangeLabel(booking.startDate, booking.endDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/app/calendar" className="btn-primary mt-5">
            Open the calendar
          </Link>
        </section>
      )}

      {(reminders.overdue.length > 0 ||
        reminders.today.length > 0 ||
        reminders.upcoming.length > 0) && (
        <section className="panel">
          {/* The margin on the ROW and none on the heading: `.row` is
              `items-end`, so a button taller than the heading otherwise hangs
              down through the heading's own mb-5 and finishes flush against
              the paragraph below. Same idiom as every `row mb-3` panel
              heading; mb-5 rather than mb-3 because the sibling panels on
              this page space an h1 from its body at the heading's own
              margin. */}
          <div className="row mb-5">
            <h1 className="!mb-0">Reminders</h1>
            <span className="spacer" />
            {isAdmin && (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setEditingThresholds(true)}
              >
                Change when these appear
              </button>
            )}
          </div>
          <p className="muted">
            Registrations, services, expiring documents and your people’s tickets, in one list,
            because they all mean the same thing. None of them stops you booking anyone; the
            decision stays yours.
          </p>

          {/* The same rows the Notifications page draws (round 38), in the
              `row` variant because this panel is already a card — the WIDER
              of the two windows, because a licence expiring in six weeks
              appears in this group and a heading reading "Next 7 days" over
              it would be the page contradicting itself. */}
          <ReminderGroups
            groups={reminders}
            horizon={Math.max(data?.leadDays ?? 7, data?.thresholds.personLeadDays ?? 60)}
            variant="row"
          />
        </section>
      )}

      <section className="panel">
        <h1>Today</h1>
        {(data?.todayBookings.length ?? 0) === 0 ? (
          <p className="muted">Nothing booked for today.</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-gray-100 p-0">
            {data?.todayBookings.map((booking) => (
              <li key={booking.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
                <span className="font-medium text-gray-900">{booking.resourceName}</span>
                <span className="text-gray-700">
                  {booking.kind === 'maintenance'
                    ? `Maintenance${booking.supplierName ? ` · ${booking.supplierName}` : ''}`
                    : `${booking.jobNumber ?? ''} ${booking.jobTitle ?? ''}`.trim()}
                </span>
                <span className="text-sm text-gray-500">
                  {booking.startTime ? `${booking.startTime}–${booking.endTime}` : 'All day'}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/app/calendar" className="btn-secondary btn-sm mt-3">
          Open the schedule
        </Link>
      </section>

      {data?.timesheets && <HomeTimesheets timesheets={data.timesheets} />}

      {data && data.recent.submissions.length > 0 && (
          <section className="panel">
            <h1>Lately</h1>
            {/* Assembled from the real tables rather than an audit log, which
                Dayboard does not have and round 16 decided not to build. */}
            {/* The WHOLE row is one link (round 35) — the leading word alone
                was a tap target a thumb misses. One anchor per row, everything
                else spans: nesting the old link inside a widened one is the
                invalid-HTML trap round 33 fell into with the tel: card. The
                leading text keeps the table-link look via a span. */}
            <ul className="m-0 list-none divide-y divide-gray-100 p-0">
              {data.recent.submissions.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/app/forms/submissions/${s.id}`}
                    className="flex min-h-11 flex-wrap items-center gap-x-1 py-2 hover:bg-gray-50 sm:min-h-0"
                  >
                    <span className="table-link">{s.templateName}</span>{' '}
                    <span className="text-gray-600">
                      on {s.about}
                      {s.byName ? ` · ${s.byName}` : ''}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

      <section className="panel">
        <h1>Getting started</h1>
        <ul className="m-0 list-none divide-y divide-gray-100 p-0">
          <Step done label="Create your account.">
            Done: {tenant.name} is set up.
          </Step>
          <Step
            done={counts.resources > 0}
            label="Add your resources."
            to="/app/resources"
          >
            Crew and plant, the things you schedule.
          </Step>
          <Step done={counts.jobs > 0} label="Create a job." to="/app/jobs">
            Attach the drawing; that is the aha moment.
          </Step>
          <Step
            done={counts.bookings > 0}
            label="Book it on the calendar."
            to="/app/calendar"
          />
          <Step
            done={invited}
            label="Invite your crew."
            to={user.role === 'admin' ? '/app/settings/users' : undefined}
          >
            {user.role === 'admin' ? '' : <em className="text-gray-600">An admin does this.</em>}
          </Step>
          <Step
            done={counts.posts > 0}
            label="Post an announcement."
            to="/app/announcements"
          >
            A safety alert or a toolbox note: the crew sees it on their phone.
          </Step>
          {/* The two the fleet and forms work added. Both are optional to a
              customer who only schedules people, which is why they sit at the
              end rather than in the middle of the original six. */}
          <Step done={counts.meters > 0} label="Add a meter to a machine." to="/app/resources/assets">
            Hours or kilometres; that is what drives service reminders.
          </Step>
          <Step
            done={counts.publishedForms > 0}
            label="Publish a form."
            to="/app/forms/templates"
          >
            A prestart or a checklist. Your crew fills it in on their phone.
          </Step>
        </ul>
      </section>

      {isAdmin && <SampleDataPanel onChanged={reload} />}

      {editingThresholds && data && (
        <ThresholdsModal
          current={data.thresholds}
          onClose={() => setEditingThresholds(false)}
          onSaved={() => {
            setEditingThresholds(false);
            reload();
          }}
        />
      )}
    </>
  );
}

/**
 * The admin's control over when reminders appear.
 *
 * Admin-only, and on Home rather than buried in settings, because the person
 * who thinks "this is nagging me too early" is looking at the nagging.
 */
function ThresholdsModal({
  current,
  onClose,
  onSaved,
}: {
  current: { warnKm: number; warnHours: number; leadDays: number; personLeadDays?: number };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [warnKm, setWarnKm] = useState(String(current.warnKm));
  const [warnHours, setWarnHours] = useState(String(current.warnHours));
  const [leadDays, setLeadDays] = useState(String(current.leadDays));
  const [personLeadDays, setPersonLeadDays] = useState(String(current.personLeadDays ?? 60));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await home.setThresholds({
        serviceWarnKm: Number(warnKm),
        serviceWarnHours: Number(warnHours),
        reminderLeadDays: Number(leadDays),
        personExpiryLeadDays: Number(personLeadDays),
      });
      onSaved();
    } catch {
      setError('Could not save those. Each one has to be a whole number above zero.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="When reminders appear"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="submit" form="thresholds" disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      <form id="thresholds" onSubmit={submit} noValidate className="legacy-page">
        <label htmlFor="lead-days">
          Days ahead
          <input
            id="lead-days"
            inputMode="numeric"
            value={leadDays}
            onChange={(e) => setLeadDays(e.target.value)}
          />
          <span className="hint">
            How far ahead a registration, a service date or an expiring document starts showing.
          </span>
        </label>

        <label htmlFor="person-lead-days">
          Days ahead: tickets and insurances
          <input
            id="person-lead-days"
            inputMode="numeric"
            value={personLeadDays}
            onChange={(e) => setPersonLeadDays(e.target.value)}
          />
          <span className="hint">
            Its own setting, and longer by default: renewing a rego is a card payment, but a
            ticket means booking a course, and a subbie’s certificate of currency means chasing
            another company. 1 to 90.
          </span>
        </label>

        <label htmlFor="warn-km">
          Kilometres before a service
          <input
            id="warn-km"
            inputMode="numeric"
            value={warnKm}
            onChange={(e) => setWarnKm(e.target.value)}
          />
        </label>

        <label htmlFor="warn-hours">
          Hours before a service
          <input
            id="warn-hours"
            inputMode="numeric"
            value={warnHours}
            onChange={(e) => setWarnHours(e.target.value)}
          />
          <span className="hint">
            A machine reading in hours warns this many hours out; one reading in kilometres uses
            the box above.
          </span>
        </label>
      </form>
    </Modal>
  );
}

/**
 * One line of the getting-started checklist.
 *
 * The state used to be a bare "✓" or "○" in the body text colour, which meant
 * the done and not-done rows looked the same from more than a foot away. A
 * filled green disc against an empty ring is a difference you can see without
 * reading — and the `sr-only` word beside it is what carries the same fact to
 * a screen reader, since a coloured circle says nothing on its own.
 */
function Step({
  done,
  label,
  to,
  children,
}: {
  done: boolean;
  label: string;
  to?: string;
  children?: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 py-3">
      <span
        className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full text-[0.7rem] font-bold ${
          done ? 'bg-green-700 text-white' : 'border-2 border-gray-300 text-transparent'
        }`}
        aria-hidden="true"
      >
        ✓
      </span>
      <span className="min-w-0">
        <span className="sr-only">{done ? 'Done: ' : 'Not done yet: '}</span>
        <strong className={done ? 'text-gray-600' : 'text-gray-900'}>
          {to ? (
            <Link to={to} className="table-link">
              {label}
            </Link>
          ) : (
            label
          )}
        </strong>{' '}
        {children}
      </span>
    </li>
  );
}

function VerifyPrompt({ onVerified }: { onVerified: () => Promise<void> }) {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Banner kind="error">
      Your email address is not confirmed yet. Without it we cannot reset your password if you get
      locked out.{' '}
      {sent ? (
        <strong>Sent. Check your inbox.</strong>
      ) : (
        <button
          className="link"
          onClick={() => {
            setError(null);
            // The chain used to end at `.then(onVerified)` with nothing after
            // it, so a send that failed was an unhandled rejection in the
            // console and, on the banner, nothing at all: the button stayed
            // where it was and the person pressed it again, waiting on an email
            // that was never going to arrive.
            void api
              .resendVerification()
              .then(() => {
                setSent(true);
                // The session refresh is a tidy-up, not part of the send, so
                // its failure is not reported as one: the email has gone.
                void onVerified().catch(() => undefined);
              })
              .catch((err: unknown) => {
                setError(
                  err instanceof ApiError
                    ? err.message
                    : 'That did not send. Try again in a moment.'
                );
              });
          }}
        >
          Send the link again
        </button>
      )}
      {error && <strong className="block mt-1">{error}</strong>}
    </Banner>
  );
}
